import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { SelectField, SwitchField, TextField } from '@/components/form-fields';
import {
  AddButton,
  EmptyState,
  ListRow,
  ListShell,
  ReadOnlyNotice,
  SectionCard,
  SettingsSkeleton,
} from '@/components/settings/settings-kit';
import { ZoneAreaMap } from '@/components/settings/zone-area-map';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/lib/auth';
import { listJurisdictions } from '@/lib/settings/fleet';
import { DAY_NAMES, listLocations } from '@/lib/settings/locations';
import {
  addWindow,
  copyWindowsToWeek,
  createZone,
  deleteWindow,
  deleteZone,
  getZoneArea,
  listWindows,
  listZones,
  type PolygonGeometry,
  updateZone,
  type ZoneRecord,
} from '@/lib/settings/zones';
import { useForm } from '@/lib/use-form';

/**
 * Delivery zones: where you deliver, what it costs, and when.
 *
 * A zone matches an address by polygon first and ZIP list second, which is
 * why the database insists on at least one of the two -- a zone with neither
 * would sit in the list refusing every address without saying so.
 *
 * Money is typed in dollars and stored in cents, converted here so the rest
 * of the pricing pipeline only ever sees integers.
 */

const money = z
  .string()
  .trim()
  .regex(/^\d+(\.\d{1,2})?$/, 'Amounts look like 12.50');

const schema = z.object({
  name: z.string().trim().min(1, 'Name the zone').max(120),
  active: z.boolean(),
  color: z
    .string()
    .trim()
    .regex(/^#[0-9a-fA-F]{6}$/, 'A colour looks like #2563eb'),
  priority: z
    .string()
    .trim()
    .regex(/^\d{1,3}$/, 'A whole number'),
  locationId: z.string(),
  zipCodes: z.string(),
  deliveryFee: money,
  deliveryMinimum: money,
  freeDeliveryOver: money.or(z.literal('')),
  leadTimeMinutes: z
    .string()
    .trim()
    .regex(/^\d{1,4}$/, 'Minutes, as a whole number'),
  cutoffTime: z.string().trim(),
  taxJurisdictionId: z.string(),
});

type Values = z.infer<typeof schema>;

const NONE = 'none';

const toDollars = (cents: number) => (cents / 100).toFixed(2);
const toCents = (value: string) => Math.round(Number(value) * 100);

function toValues(zone: ZoneRecord | null): Values {
  return {
    name: zone?.name ?? '',
    active: zone?.active ?? true,
    color: zone?.color ?? '#2563eb',
    priority: String(zone?.priority ?? 100),
    locationId: zone?.locationId ?? NONE,
    zipCodes: (zone?.zipCodes ?? []).join(', '),
    deliveryFee: toDollars(zone?.deliveryFeeCents ?? 500),
    deliveryMinimum: toDollars(zone?.deliveryMinimumCents ?? 0),
    freeDeliveryOver:
      zone?.freeDeliveryOverCents == null ? '' : toDollars(zone.freeDeliveryOverCents),
    leadTimeMinutes: String(zone?.leadTimeMinutes ?? 60),
    cutoffTime: zone?.cutoffTime ?? '',
    taxJurisdictionId: zone?.taxJurisdictionId ?? NONE,
  };
}

function parseZips(value: string): string[] {
  return [
    ...new Set(
      value
        .split(/[\s,]+/)
        .map((zip) => zip.trim())
        .filter(Boolean),
    ),
  ];
}

export function ZoneSettings({ accountId }: { accountId: string }) {
  const { can } = useAuth();
  const canManage = can('delivery.manage');
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ZoneRecord | 'new' | null>(null);

  const zones = useQuery({
    queryKey: ['delivery-zones', accountId],
    queryFn: () => listZones(accountId),
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['delivery-zones', accountId] });
    // The dispatch board paints these areas.
    queryClient.invalidateQueries({ queryKey: ['zone-areas', accountId] });
  };

  const remove = useMutation({
    mutationFn: (id: string) => deleteZone(accountId, id),
    onSuccess: () => {
      toast.success('Zone deleted.');
      setEditing(null);
      refresh();
    },
    onError: (error: Error) =>
      toast.error(
        error.message.includes('violates foreign key')
          ? 'Orders were delivered in this zone, so it cannot be deleted. Deactivate it instead.'
          : error.message,
      ),
  });

  if (zones.isLoading) return <SettingsSkeleton />;

  if (editing) {
    return (
      <div className="flex max-w-3xl flex-col gap-4">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="self-start"
          onClick={() => setEditing(null)}
        >
          <ArrowLeft className="size-4" />
          All zones
        </Button>

        <ZoneForm
          accountId={accountId}
          zone={editing === 'new' ? null : editing}
          canManage={canManage}
          onSaved={() => {
            refresh();
            setEditing(null);
          }}
          onDelete={(id) => remove.mutate(id)}
        />
      </div>
    );
  }

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      {!canManage && <ReadOnlyNotice permission="delivery.manage" />}

      <SectionCard
        title="Delivery zones"
        description="Checked in priority order: the first zone that covers an address decides its fee, minimum and time windows."
        action={
          <AddButton onClick={() => setEditing('new')} disabled={!canManage}>
            Add zone
          </AddButton>
        }
      >
        {zones.data?.length ? (
          <ListShell>
            {zones.data.map((zone) => (
              <ListRow
                key={zone.id}
                title={zone.name}
                subtitle={`Fee $${toDollars(zone.deliveryFeeCents)} · minimum $${toDollars(
                  zone.deliveryMinimumCents,
                )} · ${zone.hasArea ? 'drawn area' : `${zone.zipCodes.length} ZIP codes`} · priority ${zone.priority}`}
                badges={
                  <>
                    {zone.color && (
                      <span
                        aria-hidden
                        className="size-3 rounded-full border"
                        style={{ backgroundColor: zone.color }}
                      />
                    )}
                    {!zone.active && <Badge variant="outline">Inactive</Badge>}
                  </>
                }
                onClick={() => setEditing(zone)}
              />
            ))}
          </ListShell>
        ) : (
          <EmptyState>
            No zones yet, so the storefront refuses every delivery address. Add the area around your
            store.
          </EmptyState>
        )}
      </SectionCard>
    </div>
  );
}

function ZoneForm({
  accountId,
  zone,
  canManage,
  onSaved,
  onDelete,
}: {
  accountId: string;
  zone: ZoneRecord | null;
  canManage: boolean;
  onSaved: () => void;
  onDelete: (id: string) => void;
}) {
  const { values, errors, set, submit } = useForm(toValues(zone), schema);
  const [area, setArea] = useState<PolygonGeometry | null>(null);
  const [areaTouched, setAreaTouched] = useState(false);

  const locations = useQuery({
    queryKey: ['locations', accountId],
    queryFn: () => listLocations(accountId),
  });

  const jurisdictions = useQuery({
    queryKey: ['tax-jurisdictions', accountId],
    queryFn: () => listJurisdictions(accountId),
  });

  const stored = useQuery({
    queryKey: ['zone-area', accountId, zone?.id],
    queryFn: () => (zone ? getZoneArea(accountId, zone.id) : Promise.resolve(null)),
    enabled: Boolean(zone),
  });

  // Until the operator draws, the stored polygon is what the map shows and
  // what a save must leave alone.
  const shownArea = areaTouched ? area : (stored.data ?? null);

  const save = useMutation({
    mutationFn: async (parsed: Values) => {
      const zips = parseZips(parsed.zipCodes);
      const nextArea = areaTouched ? area : undefined;
      const willHaveArea = areaTouched ? Boolean(area) : Boolean(zone?.hasArea);

      if (!zips.length && !willHaveArea) {
        throw new Error('A zone needs a drawn area or at least one ZIP code.');
      }

      const input = {
        name: parsed.name,
        active: parsed.active,
        color: parsed.color,
        priority: Number(parsed.priority),
        locationId: parsed.locationId === NONE ? null : parsed.locationId,
        zipCodes: zips,
        deliveryFeeCents: toCents(parsed.deliveryFee),
        deliveryMinimumCents: toCents(parsed.deliveryMinimum),
        freeDeliveryOverCents:
          parsed.freeDeliveryOver === '' ? null : toCents(parsed.freeDeliveryOver),
        leadTimeMinutes: Number(parsed.leadTimeMinutes),
        cutoffTime: parsed.cutoffTime || null,
        taxJurisdictionId: parsed.taxJurisdictionId === NONE ? null : parsed.taxJurisdictionId,
        ...(nextArea !== undefined ? { area: nextArea } : {}),
      };

      if (zone) {
        await updateZone(accountId, zone.id, input);
      } else {
        await createZone(accountId, input);
      }
    },
    onSuccess: () => {
      toast.success(zone ? 'Zone saved.' : 'Zone added.');
      onSaved();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const storeOptions = [
    { value: NONE, label: 'No fulfilling store' },
    ...(locations.data ?? [])
      .filter((location) => location.type !== 'vehicle')
      .map((location) => ({ value: location.id, label: location.name })),
  ];

  const jurisdictionOptions = [
    { value: NONE, label: "The store's jurisdiction" },
    ...(jurisdictions.data ?? []).map((row) => ({ value: row.id, label: row.name })),
  ];

  const fulfillingStore = (locations.data ?? []).find(
    (location) => location.id === values.locationId,
  );

  return (
    <div className="flex flex-col gap-6">
      <form onSubmit={submit((parsed) => save.mutate(parsed))} noValidate>
        <SectionCard
          title={zone ? zone.name : 'New zone'}
          description="Draw the area, or list the ZIP codes it covers. Both is allowed: the polygon wins, and the ZIPs catch addresses the geocoder could not place."
        >
          <fieldset disabled={!canManage} className="flex flex-col gap-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                id="zone-name"
                label="Name"
                placeholder="Petaluma Core"
                value={values.name}
                error={errors.name}
                onChange={(value) => set('name', value)}
              />
              <TextField
                id="zone-priority"
                label="Priority"
                description="Lower is checked first when zones overlap."
                value={values.priority}
                error={errors.priority}
                onChange={(value) => set('priority', value)}
              />
            </div>

            <ZoneAreaMap
              value={shownArea}
              onChange={(polygon) => {
                setArea(polygon);
                setAreaTouched(true);
              }}
              center={
                fulfillingStore?.latitude && fulfillingStore.longitude
                  ? { latitude: fulfillingStore.latitude, longitude: fulfillingStore.longitude }
                  : null
              }
              disabled={!canManage}
            />

            <Field>
              <FieldLabel htmlFor="zone-zips">ZIP codes</FieldLabel>
              <Input
                id="zone-zips"
                value={values.zipCodes}
                placeholder="94952, 94954"
                onChange={(event) => set('zipCodes', event.target.value)}
              />
              <FieldDescription>
                Separated by commas. Used when an address has no coordinates.
              </FieldDescription>
            </Field>

            <div className="grid gap-4 sm:grid-cols-3">
              <TextField
                id="zone-fee"
                label="Delivery fee"
                description="Dollars."
                value={values.deliveryFee}
                error={errors.deliveryFee}
                onChange={(value) => set('deliveryFee', value)}
              />
              <TextField
                id="zone-minimum"
                label="Order minimum"
                description="Checkout refuses below this."
                value={values.deliveryMinimum}
                error={errors.deliveryMinimum}
                onChange={(value) => set('deliveryMinimum', value)}
              />
              <TextField
                id="zone-free-over"
                label="Free delivery over"
                description="Blank to always charge."
                value={values.freeDeliveryOver}
                error={errors.freeDeliveryOver}
                onChange={(value) => set('freeDeliveryOver', value)}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                id="zone-lead"
                label="Lead time (minutes)"
                description="How long before the first bookable slot."
                value={values.leadTimeMinutes}
                error={errors.leadTimeMinutes}
                onChange={(value) => set('leadTimeMinutes', value)}
              />
              <Field>
                <FieldLabel htmlFor="zone-cutoff">Same-day cutoff</FieldLabel>
                <Input
                  id="zone-cutoff"
                  type="time"
                  value={values.cutoffTime}
                  onChange={(event) => set('cutoffTime', event.target.value)}
                />
                <FieldDescription>After this, slots start tomorrow.</FieldDescription>
              </Field>
              <SelectField
                id="zone-location"
                label="Fulfilled from"
                description="Where stock is reserved and tax falls back to."
                value={values.locationId}
                options={storeOptions}
                onChange={(value) => set('locationId', value)}
              />
              <SelectField
                id="zone-jurisdiction"
                label="Tax jurisdiction"
                value={values.taxJurisdictionId}
                options={jurisdictionOptions}
                onChange={(value) => set('taxJurisdictionId', value)}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field>
                <FieldLabel htmlFor="zone-color">Map colour</FieldLabel>
                <Input
                  id="zone-color"
                  type="color"
                  className="h-10 w-20 p-1"
                  value={values.color}
                  onChange={(event) => set('color', event.target.value)}
                />
                <FieldDescription>How this zone is painted on the dispatch board.</FieldDescription>
              </Field>

              <SwitchField
                id="zone-active"
                label="Active"
                description="Inactive zones stop matching addresses immediately."
                checked={values.active}
                onChange={(checked) => set('active', checked)}
              />
            </div>

            <div className="flex items-center gap-2">
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? 'Saving…' : zone ? 'Save zone' : 'Add zone'}
              </Button>

              {zone && (
                <Button
                  type="button"
                  variant="ghost"
                  className="text-destructive"
                  onClick={() => onDelete(zone.id)}
                >
                  Delete
                </Button>
              )}
            </div>
          </fieldset>
        </SectionCard>
      </form>

      {zone && <WindowsPanel accountId={accountId} zone={zone} canManage={canManage} />}
    </div>
  );
}

function WindowsPanel({
  accountId,
  zone,
  canManage,
}: {
  accountId: string;
  zone: ZoneRecord;
  canManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState({
    dayOfWeek: '1',
    startsAt: '11:00',
    endsAt: '14:00',
    maxOrders: '8',
  });

  const windows = useQuery({
    queryKey: ['delivery-windows', accountId, zone.id],
    queryFn: () => listWindows(accountId, zone.id),
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['delivery-windows', accountId, zone.id] });

  const add = useMutation({
    mutationFn: () =>
      addWindow(accountId, {
        zoneId: zone.id,
        dayOfWeek: Number(draft.dayOfWeek),
        startsAt: draft.startsAt,
        endsAt: draft.endsAt,
        maxOrders: draft.maxOrders.trim() === '' ? null : Number(draft.maxOrders),
      }),
    onSuccess: () => {
      toast.success('Window added.');
      refresh();
    },
    onError: (error: Error) =>
      toast.error(
        error.message.includes('exclusion') || error.message.includes('overlap')
          ? 'That window overlaps another on the same day.'
          : error.message,
      ),
  });

  const copy = useMutation({
    mutationFn: () => {
      const source = (windows.data ?? []).filter(
        (row) => row.dayOfWeek === Number(draft.dayOfWeek),
      );

      if (!source.length) throw new Error('Add that day first, then copy it.');

      return copyWindowsToWeek(accountId, zone.id, source);
    },
    onSuccess: () => {
      toast.success('Copied across the week.');
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteWindow(accountId, id),
    onSuccess: refresh,
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <SectionCard
      title="Delivery windows"
      description="The slots a shopper can book in this zone. Capacity is per window per day; when it fills, the slot disappears."
    >
      {windows.data?.length ? (
        <ListShell>
          {windows.data.map((row) => (
            <ListRow
              key={row.id}
              title={`${DAY_NAMES[row.dayOfWeek]} · ${row.startsAt}–${row.endsAt}`}
              subtitle={row.maxOrders === null ? 'Unlimited' : `${row.maxOrders} orders`}
              actions={
                canManage ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => remove.mutate(row.id)}
                  >
                    Remove
                  </Button>
                ) : null
              }
            />
          ))}
        </ListShell>
      ) : (
        <EmptyState>
          No windows, so checkout offers no delivery times and orders arrive unscheduled.
        </EmptyState>
      )}

      {canManage && (
        <div className="flex flex-wrap items-end gap-3">
          <SelectField
            id="window-day"
            label="Day"
            className="w-40"
            value={draft.dayOfWeek}
            options={DAY_NAMES.map((name, index) => ({ value: String(index), label: name }))}
            onChange={(value) => setDraft((current) => ({ ...current, dayOfWeek: value }))}
          />
          <Field className="w-28">
            <FieldLabel htmlFor="window-start">From</FieldLabel>
            <Input
              id="window-start"
              type="time"
              value={draft.startsAt}
              onChange={(event) =>
                setDraft((current) => ({ ...current, startsAt: event.target.value }))
              }
            />
          </Field>
          <Field className="w-28">
            <FieldLabel htmlFor="window-end">To</FieldLabel>
            <Input
              id="window-end"
              type="time"
              value={draft.endsAt}
              onChange={(event) =>
                setDraft((current) => ({ ...current, endsAt: event.target.value }))
              }
            />
          </Field>
          <Field className="w-28">
            <FieldLabel htmlFor="window-capacity">Capacity</FieldLabel>
            <Input
              id="window-capacity"
              inputMode="numeric"
              placeholder="8"
              value={draft.maxOrders}
              onChange={(event) =>
                setDraft((current) => ({ ...current, maxOrders: event.target.value }))
              }
            />
          </Field>

          <Button type="button" size="sm" onClick={() => add.mutate()} disabled={add.isPending}>
            Add
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => copy.mutate()}
            disabled={copy.isPending}
          >
            Copy this day to the week
          </Button>
        </div>
      )}
    </SectionCard>
  );
}
